/**

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

// @mui material components
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

interface OwnerState {
  variant: "contained" | "gradient";
  bgColor: string;
  color: string;
  opacity: number;
  borderRadius: string;
  shadow: string;
}

export default styled(Box)<{ ownerState: OwnerState }>(({ theme, ownerState }) => {
  const { palette, functions, borders, boxShadows } = theme;
  const { variant, bgColor, color, opacity, borderRadius, shadow } = ownerState;

  const { gradients, grey, white } = palette;
  const { linearGradient } = functions;
  const { borderRadius: radius } = borders;

  const greyColors: Record<string, string> = {
    "grey-100": grey[100],
    "grey-200": grey[200],
    "grey-300": grey[300],
    "grey-400": grey[400],
    "grey-500": grey[500],
    "grey-600": grey[600],
    "grey-700": grey[700],
    "grey-800": grey[800],
    "grey-900": grey[900],
  };

  // Every palette key named below holds a `main` (the Vision colors add one to MUI's `text` too).
  const paletteColors = palette as unknown as Record<string, { main: string } | undefined>;

  const validGradients = [
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ] as const;

  const validColors = [
    "transparent",
    "white",
    "black",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "text",
    "grey-100",
    "grey-200",
    "grey-300",
    "grey-400",
    "grey-500",
    "grey-600",
    "grey-700",
    "grey-800",
    "grey-900",
  ];

  const validBorderRadius = ["xs", "sm", "md", "lg", "xl", "xxl", "section"] as const;
  const validBoxShadows = ["xs", "sm", "md", "lg", "xl", "xxl", "inset"] as const;

  // background value
  let backgroundValue: string | undefined = bgColor;

  if (variant === "gradient") {
    const gradient = validGradients.find((el) => el === bgColor);
    backgroundValue = gradient
      ? linearGradient(gradients[gradient].main, gradients[gradient].state)
      : white.main;
  } else if (validColors.find((el) => el === bgColor)) {
    const paletteColor = paletteColors[bgColor];
    backgroundValue = paletteColor ? paletteColor.main : greyColors[bgColor];
  } else {
    backgroundValue = bgColor;
  }

  // color value
  let colorValue: string | undefined = color;

  if (validColors.find((el) => el === color)) {
    const paletteColor = paletteColors[color];
    colorValue = paletteColor ? paletteColor.main : greyColors[color];
  }

  // borderRadius value
  let borderRadiusValue = borderRadius;
  const radiusKey = validBorderRadius.find((el) => el === borderRadius);

  if (radiusKey) {
    borderRadiusValue = radius[radiusKey];
  }

  // boxShadow value; anything else ("none") sets no shadow
  let boxShadowValue: string | undefined;
  const shadowKey = validBoxShadows.find((el) => el === shadow);

  if (shadowKey) {
    boxShadowValue = boxShadows[shadowKey];
  }

  return {
    opacity,
    background: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
  };
});
